// src/components/ProjectTopics.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, query, where, onSnapshot, addDoc, deleteDoc, updateDoc, doc, serverTimestamp, orderBy, arrayUnion, arrayRemove 
} from 'firebase/firestore';
import { db } from '../services/firebase'; 

const ProjectTopics = ({ companyId, user, companyUsers = [] }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(''); 
  const [activeTab, setActiveTab] = useState('overview'); 
  
  const [topics, setTopics] = useState([]);
  const [projectMeetings, setProjectMeetings] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicDescription, setNewTopicDescription] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');

  // 1. PROJEKTE LADEN
  useEffect(() => {
    if (!companyId || !user?.uid) return;
    
    const projectsQuery = query(
        collection(db, "companies", companyId, "projects"), 
        where("members", "array-contains", user.uid)
    );
    
    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
        const loadedProjects = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        loadedProjects.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setProjects(loadedProjects);
        setLoading(false);
    }, (error) => {
        console.error("Error loading projects:", error);
        setLoading(false);
    });
    
    return () => unsubscribe();
  }, [companyId, user?.uid]);

  const currentProject = useMemo(() => 
    projects.find(p => p.id === selectedProjectId) || null
  , [projects, selectedProjectId]);

  // 2. Daten laden
  useEffect(() => {
    if (!companyId || !selectedProjectId) {
        setTopics([]);
        setProjectMeetings([]);
        setProjectTasks([]);
        return;
    }

    // A) TOPICS
    const qTopics = query(
        collection(db, "companies", companyId, "projectTopics"), 
        where("projectId", "==", selectedProjectId)
    );
    const unsubTopics = onSnapshot(qTopics, (snapshot) => {
        const loadedTopics = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        loadedTopics.sort((a, b) => (b.createdAt?.toMillis ? b.createdAt.toMillis() : 0) - (a.createdAt?.toMillis ? a.createdAt.toMillis() : 0));
        setTopics(loadedTopics);
    });

    // B) MEETINGS
    const qMeetings = query(
        collection(db, "companies", companyId, "meetings"),
        where("projectId", "==", selectedProjectId)
    );
    const unsubMeetings = onSnapshot(qMeetings, (snapshot) => {
        const loadedMeetings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        loadedMeetings.sort((a, b) => new Date(b.date) - new Date(a.date));
        setProjectMeetings(loadedMeetings);
    });

    // C) TASKS - Alle Tasks der Firma laden (client-side filtering ist flexibler für diesen Fall)
    const qTasks = query(collection(db, "companies", companyId, "tasks"));
    const unsubTasks = onSnapshot(qTasks, (snapshot) => {
        const allTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProjectTasks(allTasks); 
    });
    
    return () => {
        unsubTopics();
        unsubMeetings();
        unsubTasks();
    };
  }, [companyId, selectedProjectId]);

  // FIX: Filter erweitert um 'projectId' (für Tasks, die direkt dem Projekt zugewiesen sind)
  const filteredProjectTasks = useMemo(() => {
      const meetingIds = new Set(projectMeetings.map(m => m.id));
      
      return projectTasks.filter(t => 
          // 1. Task gehört zu einem Meeting dieses Projekts
          (t.meetingId && meetingIds.has(t.meetingId)) || 
          // 2. Task ist direkt dem Projekt zugewiesen (via ID)
          (t.projectId === selectedProjectId) ||
          // 3. Fallback: Task ist dem Projekt zugewiesen (via Name - für ältere Tasks)
          (t.project && currentProject && t.project === currentProject.name)
      );
  }, [projectTasks, projectMeetings, selectedProjectId, currentProject]);

  const handleAddTopic = async (e) => { e.preventDefault(); if ((!newTopicTitle.trim() && !newTopicDescription.trim()) || !selectedProjectId) return; try { await addDoc(collection(db, "companies", companyId, "projectTopics"), { title: newTopicTitle.trim(), description: newTopicDescription.trim(), projectId: selectedProjectId, projectName: currentProject?.name || '', status: 'open', createdBy: (user.firstName || '') + ' ' + (user.lastName || ''), creatorId: user.uid, createdAt: serverTimestamp() }); setNewTopicTitle(''); setNewTopicDescription(''); } catch (error) { alert("Fehler: " + error.message); } };
  const handleDeleteTopic = async (topicId) => { if (window.confirm("Löschen?")) { await deleteDoc(doc(db, "companies", companyId, "projectTopics", topicId)); } };
  const handleReopenTopic = async (topicId) => { await updateDoc(doc(db, "companies", companyId, "projectTopics", topicId), { status: 'open', assignedMeetingId: null, assignedMeetingTitle: null, assignedMeetingDate: null }); };
  const saveEdit = async (topicId) => { await updateDoc(doc(db, "companies", companyId, "projectTopics", topicId), { title: editTitle, description: editDescription }); setEditingTopicId(null); setEditTitle(''); setEditDescription(''); };
  const handleCreateNewProject = async () => { const name = prompt("Name des neuen Projekts:"); if (name && name.trim()) { try { const docRef = await addDoc(collection(db, "companies", companyId, "projects"), { name: name.trim(), members: [user.uid], createdBy: user.uid, createdAt: serverTimestamp() }); setSelectedProjectId(docRef.id); } catch (error) { console.error("Error:", error); alert("Fehler."); } } };
  const handleAddMember = async () => { if (!selectedUserToAdd || !selectedProjectId) return; try { await updateDoc(doc(db, "companies", companyId, "projects", selectedProjectId), { members: arrayUnion(selectedUserToAdd) }); setSelectedUserToAdd(''); } catch (error) { alert("Fehler."); } };
  const handleRemoveMember = async (memberUid) => { if (!selectedProjectId || !window.confirm("Entfernen?")) return; try { await updateDoc(doc(db, "companies", companyId, "projects", selectedProjectId), { members: arrayRemove(memberUid) }); if (memberUid === user.uid) setSelectedProjectId(''); } catch (error) { alert("Fehler."); } };

  const projectMembers = useMemo(() => {
      if (!currentProject || !currentProject.members) return [];
      return currentProject.members.map(uid => {
          const userDetails = companyUsers.find(u => u.id === uid) || {};
          return { uid, name: userDetails.firstName ? `${userDetails.firstName} ${userDetails.lastName}` : (userDetails.email || 'Unbekannt'), department: userDetails.department || 'Keine Abteilung', email: userDetails.email, photoURL: userDetails.photoURL };
      });
  }, [currentProject, companyUsers]);

  const availableUsersToAdd = useMemo(() => {
      if (!currentProject) return [];
      const currentMemberSet = new Set(currentProject.members || []);
      return companyUsers.filter(u => !currentMemberSet.has(u.id));
  }, [companyUsers, currentProject]);

  const openTopics = topics.filter(t => t.status === 'open');
  const assignedTopics = topics.filter(t => t.status === 'assigned');

  const TabButton = ({ id, label, icon }) => (
      <button onClick={() => setActiveTab(id)} className={`px-4 py-2 rounded-lg font-medium flex items-center transition-all ${activeTab === id ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
          <span className="mr-2">{icon}</span> {label}
      </button>
  );

  const renderContent = () => {
      switch(activeTab) {
        case 'overview':
            return (
                <div className="space-y-8 animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
                            <h4 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase">Offene Themen</h4>
                            <p className="text-4xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-2">{openTopics.length}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
                            <h4 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase">Protokolle</h4>
                            <p className="text-4xl font-extrabold text-blue-600 dark:text-blue-400 mt-2">{projectMeetings.length}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
                            <h4 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase">Aufgaben (Offen)</h4>
                            <p className="text-4xl font-extrabold text-orange-500 mt-2">
                                {filteredProjectTasks.filter(t => t.status !== 'Erledigt').length}
                            </p>
                        </div>
                    </div>
                    {/* Team Section */}
                    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                            <h4 className="font-bold text-lg text-gray-800 dark:text-white flex items-center">👥 Projekt-Team <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-200 dark:bg-gray-600 px-2 rounded-full">{projectMembers.length}</span></h4>
                            <div className="flex gap-2">
                                <select value={selectedUserToAdd} onChange={(e) => setSelectedUserToAdd(e.target.value)} className="p-2 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-500 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500">
                                    <option value="">Mitarbeiter auswählen...</option>
                                    {availableUsersToAdd.map(u => (<option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>))}
                                </select>
                                <button onClick={handleAddMember} disabled={!selectedUserToAdd} className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">+ Hinzufügen</button>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-600">
                            {projectMembers.length === 0 ? <p className="p-6 text-center text-gray-400">Keine Mitglieder.</p> : projectMembers.map(member => (
                                <div key={member.uid} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600/50 transition">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold text-sm overflow-hidden">{member.photoURL ? <img src={member.photoURL} alt={member.name} className="w-full h-full object-cover" /> : member.name.charAt(0)}</div>
                                        <div><p className="font-semibold text-gray-900 dark:text-white">{member.name}</p><div className="flex items-center text-xs text-gray-500 dark:text-gray-400"><span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded mr-2 border border-blue-100 dark:border-blue-800">{member.department}</span><span>{member.email}</span></div></div>
                                    </div>
                                    <button onClick={() => handleRemoveMember(member.uid)} className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition" title="Entfernen">✕</button>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Letzte Protokolle */}
                    <div className="mt-4">
                        <h4 className="font-bold text-lg dark:text-white mb-3">Letzte Protokolle</h4>
                        {projectMeetings.length === 0 ? <p className="text-gray-400 italic">Keine Protokolle vorhanden.</p> : (
                            <div className="space-y-2">
                                {projectMeetings.slice(0, 3).map(m => (
                                    <a key={m.id} href={`#/protokoll/${m.id}`} className="block p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600 hover:border-indigo-400 transition flex justify-between items-center">
                                        <div>
                                            <span className="font-bold text-gray-800 dark:text-white">{m.title}</span>
                                            <span className="text-sm text-gray-500 ml-2">({new Date(m.date).toLocaleDateString()})</span>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded text-xs ${m.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {m.status === 'completed' ? 'Abgeschlossen' : 'Planung'}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        case 'topics':
            return (
                <div className="space-y-6 animate-fadeIn">
                     <form onSubmit={handleAddTopic} className="flex flex-col gap-2 mb-6 bg-white dark:bg-gray-700 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
                        <input type="text" value={newTopicTitle} onChange={(e) => setNewTopicTitle(e.target.value)} placeholder="Titel (z.B. Budget)" className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white font-bold" />
                        <textarea value={newTopicDescription} onChange={(e) => setNewTopicDescription(e.target.value)} placeholder="Details..." className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white min-h-[80px]" />
                        <button type="submit" className="self-end px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 shadow-md">Hinzufügen</button>
                    </form>
                    <div className="space-y-4">
                        {openTopics.map(topic => (
                            <div key={topic.id} className="flex items-start justify-between p-4 bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
                                <div className="flex-grow mr-4">
                                    {editingTopicId === topic.id ? (
                                        <div className="flex flex-col gap-2">
                                            <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full p-2 border rounded dark:bg-gray-600 dark:text-white font-bold" />
                                            <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} className="w-full p-2 border rounded dark:bg-gray-600 dark:text-white" />
                                            <div className="flex gap-2 mt-1"><button onClick={() => saveEdit(topic.id)} className="px-3 py-1 bg-green-600 text-white rounded text-sm font-bold">Speichern</button><button onClick={() => setEditingTopicId(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm">Abbrechen</button></div>
                                        </div>
                                    ) : (
                                        <>{topic.title && <p className="font-bold text-gray-900 dark:text-white text-lg">{topic.title}</p>}<p className="text-gray-600 dark:text-gray-300 mt-1">{topic.description}</p></>
                                    )}
                                </div>
                                <div className="flex gap-2"><button onClick={() => { setEditingTopicId(topic.id); setEditTitle(topic.title||''); setEditDescription(topic.description); }} className="text-gray-400 hover:text-blue-500">✏️</button><button onClick={() => handleDeleteTopic(topic.id)} className="text-gray-400 hover:text-red-500">🗑️</button></div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        case 'meetings': return (<div className="space-y-4 animate-fadeIn">{projectMeetings.map(m => (<a key={m.id} href={`#/protokoll/${m.id}`} className="block p-5 bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 hover:border-indigo-400 transition"><h4 className="font-bold text-xl text-gray-800 dark:text-white">{m.title}</h4><span className="text-sm text-gray-500">{new Date(m.date).toLocaleDateString()}</span></a>))}</div>);
        case 'tasks':
            return (
                <div className="space-y-6 animate-fadeIn">
                    {['Offen', 'In Bearbeitung', 'Erledigt'].map(status => {
                        const tasksInStatus = filteredProjectTasks.filter(t => {
                            if (status === 'Erledigt') return t.status === 'Erledigt' || t.isCompleted;
                            if (status === 'In Bearbeitung') return t.status === 'In Bearbeitung';
                            return t.status !== 'Erledigt' && t.status !== 'In Bearbeitung' && !t.isCompleted;
                        });
                        if (tasksInStatus.length === 0 && status !== 'Offen') return null;
                        return (
                            <div key={status}>
                                <h4 className={`font-bold mb-3 border-b pb-1 ${status === 'Erledigt' ? 'text-green-600' : 'text-gray-700 dark:text-gray-300'}`}>{status} ({tasksInStatus.length})</h4>
                                <div className="space-y-2">
                                    {tasksInStatus.map(task => (
                                        <div key={task.id} className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm flex justify-between">
                                            <div><p className="font-medium dark:text-white">{task.title}</p><p className="text-xs text-gray-500">Fällig: {task.dueDate || 'Kein Datum'}</p></div>
                                            <div className="text-right"><span className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">{task.assignees?.map(a => a.name).join(', ') || 'Niemand'}</span></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        default: return null;
      }
  };

  return (
      <div className="flex flex-col md:flex-row h-[calc(100vh-100px)] bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="w-full md:w-72 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
              <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center"><h3 className="font-bold text-lg dark:text-white">Projekte</h3><button onClick={handleCreateNewProject} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-bold">+</button></div>
              <div className="overflow-y-auto flex-grow p-2 space-y-1">
                  {projects.map(p => (<button key={p.id} onClick={() => setSelectedProjectId(p.id)} className={`w-full text-left px-4 py-3 rounded-lg transition-all ${selectedProjectId === p.id ? 'bg-indigo-600 text-white font-bold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>{p.name}</button>))}
              </div>
          </div>
          <div className="flex-grow flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
              {!selectedProjectId ? <div className="flex items-center justify-center h-full text-gray-400">Kein Projekt ausgewählt</div> : <><div className="bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-700 shadow-sm z-10"><h2 className="text-3xl font-extrabold mb-4">{currentProject?.name}</h2><div className="flex space-x-2"><TabButton id="overview" label="Übersicht" icon="📊" /><TabButton id="topics" label="Themen" icon="💡" /><TabButton id="meetings" label="Protokolle" icon="📅" /><TabButton id="tasks" label="Aufgaben" icon="✅" /></div></div><div className="flex-grow overflow-y-auto p-6">{renderContent()}</div></>}
          </div>
      </div>
  );
};

export default ProjectTopics;